import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';

const CROSSFADE_MS = 450;
type Owner = object;
// opaque: the backdrop has finished any entrance fade, so nothing behind it shows through.
type Backdrop = { route: string; element: HTMLElement; swatch: string; hidden: boolean; ready: boolean; opaque: boolean };
type Content = { route: string; ready: boolean; unthemed: boolean };
type RetainedFrame = { route: string; swatch: string; canvas: HTMLCanvasElement | null };

const isModulePath = (pathname: string) => /^\/(?:flows\/[^/]+|courses\/[^/]+\/flows\/[^/]+|quiz-results\/[^/]+)\/?$/.test(pathname);

interface TransitionContextValue {
  route: string;
  eligible: boolean;
  isRetainingTheme: boolean;
  registerBackdrop: (owner: Owner, backdrop: Backdrop) => () => void;
  updateBackdrop: (owner: Owner, ready: boolean, hidden: boolean, opaque: boolean) => void;
  registerContent: (owner: Owner, content: Content) => () => void;
}

const TransitionContext = createContext<TransitionContextValue | null>(null);

/** Copy just the scene's pixels. Never retain cards, controls, Lottie bursts or an active scene loop. */
const copyFrame = ({ route, element, swatch }: Backdrop): RetainedFrame => {
  let canvas: HTMLCanvasElement | null = null;
  const source = element.querySelector('canvas');
  if (source && source.width && source.height) {
    try {
      const copy = document.createElement('canvas');
      copy.width = source.width;
      copy.height = source.height;
      const context = copy.getContext('2d');
      if (context) {
        context.drawImage(source, 0, 0);
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('data-testid', 'flow-theme-retained-canvas');
        Object.assign(copy.style, { width: '100%', height: '100%', display: 'block' });
        canvas = copy;
      }
    } catch {
      // Canvas support and scene failures still leave the outgoing theme's swatch.
    }
  }
  return { route, swatch, canvas };
};

type FlowThemeTransitionProviderProps = {
  children: React.ReactNode;
  // Reports whether a ready scene or retained frame fully covers the route background for a history entry.
  onCoverageChange?: (locationKey: string, covered: boolean) => void;
};

/** Lives inside the page's stacking context so the frame covers backdrops but stays behind controls. */
export const FlowThemeTransitionProvider: React.FC<FlowThemeTransitionProviderProps> = ({ children, onCoverageChange }) => {
  const location = useLocation();
  const route = `${location.key}:${location.pathname}${location.search}`;
  const eligible = isModulePath(location.pathname);
  const initialReducedMotion = useReducedMotion();
  const [observedReducedMotion, setObservedReducedMotion] = useState<boolean | null>(() =>
    typeof window !== 'undefined' ? window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? null : null);
  const reducedMotion = observedReducedMotion ?? Boolean(initialReducedMotion);
  const backdrops = useRef(new Map<Owner, Backdrop>());
  const content = useRef(new Map<Owner, Content>());
  const [, setRevision] = useState(0);
  const [retained, setRetained] = useState<RetainedFrame | null>(null);
  const [fading, setFading] = useState(false);
  const frameHost = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    setObservedReducedMotion(query.matches);
    const changed = (event: MediaQueryListEvent) => setObservedReducedMotion(event.matches);
    if (query.addEventListener) {
      query.addEventListener('change', changed);
      return () => query.removeEventListener('change', changed);
    }
    query.addListener?.(changed);
    return () => query.removeListener?.(changed);
  }, []);

  const registerBackdrop = useCallback((owner: Owner, backdrop: Backdrop) => {
    backdrops.current.set(owner, backdrop);
    setRevision(value => value + 1);
    return () => {
      const outgoing = backdrops.current.get(owner);
      backdrops.current.delete(owner);
      // Layout cleanup runs while the canvas still exists, before its passive
      // driver disposal. Same-route remounts are captured but never displayed.
      if (outgoing && outgoing.ready && !outgoing.hidden) {
        setRetained(copyFrame(outgoing));
        setFading(false);
      }
      setRevision(value => value + 1);
    };
  }, []);

  const updateBackdrop = useCallback((owner: Owner, ready: boolean, hidden: boolean, opaque: boolean) => {
    const backdrop = backdrops.current.get(owner);
    if (backdrop && (backdrop.ready !== ready || backdrop.hidden !== hidden || backdrop.opaque !== opaque)) {
      backdrop.ready = ready;
      backdrop.hidden = hidden;
      backdrop.opaque = opaque;
      setRevision(value => value + 1);
    }
  }, []);

  const registerContent = useCallback((owner: Owner, readiness: Content) => {
    content.current.set(owner, readiness);
    setRevision(value => value + 1);
    return () => {
      content.current.delete(owner);
      setRevision(value => value + 1);
    };
  }, []);

  const isRetainingTheme = Boolean(eligible && retained && retained.route !== route);
  const readyContent = Array.from(content.current.values()).filter(item => item.route === route && item.ready);
  const destinationReady = readyContent.some(item => item.unthemed)
    || (readyContent.length > 0 && Array.from(backdrops.current.values()).some(item => item.route === route && item.ready && !item.hidden));

  // Only fully opaque layers count: a scene still fading in, or a retained frame fading out, shows the route art.
  const coversRouteBackground = (isRetainingTheme && !fading)
    || Array.from(backdrops.current.values()).some(item => item.route === route && item.opaque && !item.hidden);

  useLayoutEffect(() => {
    onCoverageChange?.(location.key, coversRouteBackground);
  }, [onCoverageChange, location.key, coversRouteBackground]);

  useLayoutEffect(() => {
    setFading(false);
    if (!eligible) setRetained(null);
  }, [route, eligible]);

  useLayoutEffect(() => {
    if (retained?.route === route || !eligible) setRetained(null);
  }, [retained, route, eligible]);

  useLayoutEffect(() => {
    if (!destinationReady) setFading(false);
  }, [destinationReady]);

  useLayoutEffect(() => {
    const host = frameHost.current;
    if (!host || !retained?.canvas) return undefined;
    host.appendChild(retained.canvas);
    return () => { retained.canvas?.remove(); };
  }, [retained, isRetainingTheme]);

  useEffect(() => {
    if (!isRetainingTheme || !destinationReady) return undefined;
    if (reducedMotion) {
      setRetained(null);
      return undefined;
    }
    // Force the retained frame's opaque start before asking the compositor to
    // fade it. A ready cached destination can otherwise skip the transition.
    frameHost.current?.getBoundingClientRect();
    setFading(true);
    const timer = window.setTimeout(() => setRetained(current => current === retained ? null : current), CROSSFADE_MS);
    return () => window.clearTimeout(timer);
  }, [destinationReady, isRetainingTheme, reducedMotion, retained, route]);

  const value = useMemo(() => ({ route, eligible, isRetainingTheme, registerBackdrop, updateBackdrop, registerContent }),
    [route, eligible, isRetainingTheme, registerBackdrop, updateBackdrop, registerContent]);

  return (
    <TransitionContext.Provider value={value}>
      {isRetainingTheme && retained ? (
        <div
          ref={frameHost}
          aria-hidden="true"
          data-testid="flow-theme-transition"
          data-flow-theme-transition-fading={fading ? 'true' : 'false'}
          className="pointer-events-none fixed inset-0 overflow-hidden"
          style={{ zIndex: -5, background: retained.swatch, opacity: fading ? 0 : 1, transition: reducedMotion || !destinationReady ? 'none' : `opacity ${CROSSFADE_MS}ms ease-out` }}
        />
      ) : null}
      {children}
    </TransitionContext.Provider>
  );
};

export const useFlowThemeTransition = (): { isRetainingTheme: boolean } => {
  const context = useContext(TransitionContext);
  return { isRetainingTheme: context?.isRetainingTheme ?? false };
};

/** Register a mounted backdrop; cleanup retains its final painted frame across module navigation. */
export const useFlowThemeTransitionBackdrop = (
  ref: React.RefObject<HTMLElement>,
  { swatch, ready, hidden = false, opaque = ready }: { swatch: string; ready: boolean; hidden?: boolean; opaque?: boolean },
) => {
  const context = useContext(TransitionContext);
  const register = context?.registerBackdrop;
  const update = context?.updateBackdrop;
  const route = context?.route;
  const eligible = context?.eligible;
  const owner = useRef<Owner>({});
  useLayoutEffect(() => {
    if (!register || !route || !eligible || !ref.current) return undefined;
    return register(owner.current, { route, element: ref.current, swatch, ready: false, hidden: true, opaque: false });
  }, [register, route, eligible, ref, swatch]);
  // Readiness changes update the registration without capturing/remounting it.
  useLayoutEffect(() => { update?.(owner.current, ready, hidden, opaque); }, [update, route, ready, hidden, opaque]);
};

/** A runtime reports when opening content (or an actionable error) is ready. */
export const useFlowThemeTransitionReady = (ready: boolean, { unthemed = false }: { unthemed?: boolean } = {}) => {
  const context = useContext(TransitionContext);
  const register = context?.registerContent;
  const route = context?.route;
  const owner = useRef<Owner>({});
  useLayoutEffect(() => {
    if (!register || !route) return undefined;
    return register(owner.current, { route, ready, unthemed });
  }, [register, route, ready, unthemed]);
};
